// Чистая модель строки матча (по MatchRowView из Cricket iOS). Покрыта тестами на живых фикстурах.
import type { MatchRow, TeamRef } from '../api/types';

export interface RowTeam {
  team: TeamRef;
  /** Счёт команды: серверный score_text; в старом ответе (без ключа) — display её иннингсов через « & ». null — не била. */
  score: string | null;
  isWinner: boolean;
  /** Приглушить: итог известен и команда не победила. */
  isDimmed: boolean;
  /** Бьёт сейчас — только у живого матча, по последнему иннингсу. */
  isBattingNow: boolean;
}

export type StatusKind = 'live' | 'state' | 'start' | 'note' | 'label';

export interface RowModel {
  meta: string[];
  teams: RowTeam[];
  isLive: boolean;
  status: { kind: StatusKind; text: string };
}

/** Команды в порядке иннингсов (кто бил первым — сверху); без иннингсов — home, away. */
export function orderedTeams(match: MatchRow): TeamRef[] {
  const teams = match.teams;
  if (!teams) return [];
  const pair = [teams.home, teams.away];
  const firstId = match.innings?.[0]?.team_id;
  if (firstId === teams.away.id) return [teams.away, teams.home];
  return pair;
}

export function buildRowModel(match: MatchRow, opts: { showLeague: boolean; formatStart: (iso: string) => string; context?: string }): RowModel {
  const innings = match.innings ?? [];
  const winner = match.winner_team_id ?? null;
  const lastInnings = innings[innings.length - 1];
  const teams = orderedTeams(match).map<RowTeam>((team) => {
    const own = innings.filter((i) => i.team_id === team.id).map((i) => i.display);
    return {
      team,
      score: 'score_text' in team ? team.score_text ?? null : own.length ? own.join(' & ') : null,
      isWinner: winner !== null && winner === team.id,
      isDimmed: winner !== null && winner !== team.id,
      isBattingNow: match.is_live && lastInnings?.team_id === team.id,
    };
  });

  const meta = [opts.showLeague ? match.league?.name : null, match.round, match.venue?.name]
    // То, что уже сказано заголовком карточки (раунд плей-офф), в строке не повторяем.
    .filter((part): part is string => Boolean(part) && part !== opts.context);

  // Приоритет текста статуса: state.text → время старта (не начался) → note → status_label.
  let status: RowModel['status'];
  const notStarted = !match.is_live && innings.length === 0 && winner === null;
  if (match.state?.text) status = { kind: match.is_live ? 'live' : 'state', text: match.state.text };
  else if (notStarted) status = { kind: 'start', text: opts.formatStart(match.starting_at) };
  else if (match.note) status = { kind: match.is_live ? 'live' : 'note', text: match.note };
  else status = { kind: match.is_live ? 'live' : 'label', text: match.status_label };

  return { meta, teams, isLive: match.is_live, status };
}

/** Подпись старта не начавшегося матча (HQ-1735): день старта — сегодня по местному времени или строка стоит под
 *  заголовком дня (timeOnly) → только время; иначе короткая дата и время одним Intl-вызовом («2 Nov, 14:30»),
 *  как .dateAndLeague в iOS: в «Ближайших», турнире и поиске матч через 3 дня не должен выглядеть сегодняшним. */
export function formatStartText(iso: string, opts: {
  now: Date; timeOnly?: boolean; format: (date: Date, options: Intl.DateTimeFormatOptions) => string;
}): string {
  const start = new Date(iso);
  const time: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit', hour12: false };
  const today = start.getFullYear() === opts.now.getFullYear() && start.getMonth() === opts.now.getMonth()
    && start.getDate() === opts.now.getDate();
  return opts.format(start, opts.timeOnly || today ? time : { day: 'numeric', month: 'short', ...time });
}

/** Та же подпись старта для вёрстки (HQ-1735 v2): дата и время порознь — строка с датой рисует их двумя строками,
 *  чтобы на крупном шрифте в hi/ta перенос даты не срезал время. date === null — только время (сегодня или timeOnly). */
export function formatStartParts(iso: string, opts: Parameters<typeof formatStartText>[1]): { date: string | null; time: string } {
  const start = new Date(iso);
  const time = opts.format(start, { hour: '2-digit', minute: '2-digit', hour12: false });
  const today = start.getFullYear() === opts.now.getFullYear() && start.getMonth() === opts.now.getMonth()
    && start.getDate() === opts.now.getDate();
  return { date: opts.timeOnly || today ? null : opts.format(start, { day: 'numeric', month: 'short' }), time };
}
